// El marco de la columna 2 de la ventana «Santi» (propuesta §5.1, §5.3, P1/P3/P9), igual para un contrato, el chat y
// «Pedir algo»: la cabecera de 72 (título 18 y una línea 15; a la derecha, lo del contrato y después lo de la ventana:
// raya, «Tu agente» y ✕), el cuerpo, la caja de escribir abajo, la línea flotante de «Hecho»/«Cancelar» sobre la caja, y
// el cajón del agente: empuja (escritorio) o tapa de la cabecera a la caja (iPad).
import { Bot, ChevronLeft, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { BotonIcono } from '../ui';
import { useHablaComoPau } from './store';

export function MarcoColumna({
  cabecera,
  cuerpo,
  pie,
  linea,
  cajon,
  cajonEncima = false,
  className,
}: {
  cabecera: ReactNode;
  cuerpo: ReactNode;
  pie: ReactNode;
  /** La línea flotante de «Hecho» / «Cancelar» (abajo, sobre la caja). */
  linea?: ReactNode;
  /** El cajón «Tu agente», si está abierto. */
  cajon?: ReactNode;
  /** iPad y menos: el cajón tapa la conversación (de la cabecera a la caja) en vez de empujarla. */
  cajonEncima?: boolean;
  className?: string;
}) {
  return (
    <section className={cn('flex min-h-0 min-w-0 flex-col bg-oficina-fondo', className)} data-testid="charla-santi" aria-label={t('oficinaV2.pelotas.ventana.titulo')}>
      {cabecera}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="relative min-h-0 flex-1">
            {cuerpo}
            {linea ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center px-6">
                <div className="pointer-events-auto w-full max-w-[800px]">{linea}</div>
              </div>
            ) : null}
            {cajon && cajonEncima ? <div className="absolute inset-y-0 right-0 z-20 w-[380px] max-w-full shadow-2xl shadow-black/60 max-md:w-full">{cajon}</div> : null}
          </div>
          {pie}
        </div>
        {cajon && !cajonEncima ? <div className="w-[380px] shrink-0">{cajon}</div> : null}
      </div>
    </section>
  );
}

/** La cabecera de la columna 2: título y línea 2 a la izquierda; lo del contrato (`acciones`) y lo de la ventana a la derecha. */
export function CabeceraSanti({
  titulo,
  sub,
  subCompleta,
  acciones,
  ancha,
  cajonAbierto,
  alCajon,
  alCerrar,
  alVerLista,
}: {
  titulo: string;
  sub: ReactNode;
  /** La línea 2 entera (el `title` cuando no cabe toda). */
  subCompleta?: string;
  acciones?: ReactNode;
  /** Escritorio (≥ 1280): «Tu agente» con su nombre; iPad, solo el icono. */
  ancha: boolean;
  cajonAbierto: boolean;
  alCajon: () => void;
  alCerrar: () => void;
  /** Móvil: volver a la lista. */
  alVerLista?: () => void;
}) {
  const comoPau = useHablaComoPau();
  const nombreAgente = t(comoPau ? 'oficinaV2.pelotas.agente.titulo' : 'oficinaV2.pelotas.agente.dePau');
  return (
    <header className="flex h-[72px] shrink-0 items-center gap-2 border-b border-oficina-borde bg-oficina-panel pl-6 pr-3 max-md:pl-2" data-testid="charla-cabecera">
      {alVerLista ? <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.pelotas.ventana.verLista')} onClick={alVerLista} className="h-11 w-11" /> : null}
      <div className="mr-2 min-w-0 flex-1">
        <h2 className="truncate text-[18px] font-semibold leading-6 text-oficina-texto" data-testid="charla-titulo">
          {titulo}
        </h2>
        <p className="mt-0.5 truncate text-[15px] leading-5 text-oficina-texto2" title={subCompleta} data-testid="charla-sub">
          {sub}
        </p>
      </div>
      {acciones}
      <span className="mx-1 h-6 w-px shrink-0 bg-white/10" aria-hidden />
      {ancha ? (
        <button
          type="button"
          onClick={alCajon}
          aria-pressed={cajonAbierto}
          className={cn(
            'inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-[10px] px-3 text-[16px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white/85',
            cajonAbierto ? 'bg-white/10 text-oficina-texto ring-1 ring-inset ring-white/[0.18]' : 'text-oficina-texto2 hover:bg-white/[0.06] hover:text-oficina-texto',
          )}
          data-testid="boton-tu-agente"
        >
          <Bot className="h-[18px] w-[18px]" aria-hidden />
          {nombreAgente}
        </button>
      ) : (
        <BotonIcono icono={Bot} etiqueta={nombreAgente} onClick={alCajon} aria-pressed={cajonAbierto} activo={cajonAbierto} className="h-11 w-11" data-testid="boton-tu-agente" />
      )}
      <BotonIcono icono={X} etiqueta={t('oficinaV2.pelotas.ventana.cerrar')} onClick={alCerrar} className="h-11 w-11" data-testid="cerrar-ventana" />
    </header>
  );
}
